import { createSelector } from 'reselect';
import { getNewDate, DATE_FORMAT_FULL_WITH_TIME } from 'utils/date';
import { getMetricName } from 'utils/metrics';
import * as widgetTypes from './utils/dashboardWidgetTypes';
import {
  getCampaignWidgetFilterString,
  getWidgetDataIdentifier,
  getWidgetDataType,
  getWidgetMetric,
  getWidgetType,
  NULL_METRIC,
} from './utils/dashboardWidgetUtils';
import * as d from './utils/dataTypes';

/*------------------------------------------------------------------------------------------------*/
/* Dashboard Selectors                                                                            */
/*------------------------------------------------------------------------------------------------*/
/**
 * dashboardSelector
 * Selector for the dashboard state.
 * @param { Object } state: The application state.
 * @returns { Object } The dashboard state.
 */
export const dashboardSelector = (state) => state.dashboardState;

export const propsSelector = (state, props) => props;

/**
 * createDashboardSelector
 * Creates a selector for the dashboard state.
 * @param { Object } attr: The attribute to get from the dashboard state.
 * @returns { Selector } Selector for the dashboard state of the given attribute.
 */
export const createDashboardSelector = (attr) =>
  createSelector(dashboardSelector, (dashboard) => dashboard[attr]);

/**
 * Selectors for the dashboard state.
 * @param { Object } state: The application state.
 * @returns { Any } The value of the determined dashboard state.
 */
export const selectAddEditModalOpen = createDashboardSelector('addEditModalOpen');
export const selectAddMode = createDashboardSelector('addMode');
export const selectCampaignFetchParamData = createDashboardSelector('campaignFetchParamData');
export const selectDashboardData = createDashboardSelector('dashboardData');
export const selectDefaultEndRange = createDashboardSelector('defaultEndRange');
export const selectDefaultStartRange = createDashboardSelector('defaultStartRange');
export const selectDeleteModalOpen = createDashboardSelector('deleteModalOpen');
export const selectEndDate = createDashboardSelector('endDate');
export const selectEndTime = createDashboardSelector('endTime');
export const selectFetchingCampaignsTableIds = createDashboardSelector('fetchingCampaignsTableIds');
export const selectFetchingDashboard = createDashboardSelector('fetchingDashboard');
export const selectFetchingData = createDashboardSelector('fetchingData');
export const selectLayout = createDashboardSelector('layout');
export const selectLayoutLoading = createDashboardSelector('layoutLoading');
export const selectSelectedWidget = createDashboardSelector('selectedWidget');
export const selectSettingsModalOpen = createDashboardSelector('settingsModalOpen');
export const selectStartDate = createDashboardSelector('startDate');
export const selectStartTime = createDashboardSelector('startTime');
export const selectTheme = createDashboardSelector('theme');
export const selectWidgets = createDashboardSelector('widgets');

/*------------------------------------------------------------------------------------------------*/
/* Dashboard Widget Data Formatters                                                               */
/*------------------------------------------------------------------------------------------------*/
/**
 * formatMetricDataForWidget
 * Formats metric data from a widget to use for highcharts charts.
 * @param { Object } widgetMetricData: Metric data from a widget.
 * @returns { Array } The formatted metric data.
 */
export const formatMetricDataForWidget = (widgetMetricData) => {
  const { histogram } = widgetMetricData || { histogram: null };
  const { buckets } = histogram || { buckets: [] };
  return buckets.map(({ time, value }) => ({
    name: getNewDate({ date: time }).format(DATE_FORMAT_FULL_WITH_TIME),
    x: time,
    y: value,
    marker: {
      radius: 0.2,
      states: {
        hover: {
          radius: 4,
          radiusPlus: 0,
        },
      },
    },
  }));
};

/**
 * formatMetricWidgetData
 * Formats widget data to use for highcharts charts.
 * @param { Object } widgetData: Fetched widget data containing metrics values.
 * @param { String } dataType: The data type of the widgetData.
 * @returns { Object } The formatted widget data.
 */
export const formatMetricWidgetData = (widgetData, dataType) => {
  if (widgetData && dataType !== d.PERCENTAGE) {
    const { metrics } = widgetData;
    // Maps through the metric data to format data for highcharts.
    return {
      data: metrics.map(({ data, metric, previousData }) => ({
        name: metric.name,
        data: formatMetricDataForWidget(data),
        currentValue: data && data.value,
        previousValue: previousData && previousData.value,
        metric,
      })),
    };
  }
  return widgetData;
};

/*------------------------------------------------------------------------------------------------*/
/* Dashboard Widget Selectors                                                                     */
/*------------------------------------------------------------------------------------------------*/
/**
 * widgetSelector
 * Selector for a particular widget.
 * @param { Object } state: The application state.
 * @param { Object } props: Props containg i or id of the widget to get.
 * @returns { Object } The state of the widget matching the given props.
 */
export const widgetSelector = createSelector(
  [selectWidgets, propsSelector],
  (widgets, { i, id }) =>
    widgets[id || i] || {
      settings: { visualization: '', metric: NULL_METRIC },
    },
);

export const selectWidgetType = createSelector(widgetSelector, (widget) => getWidgetType(widget));

export const selectWidgetDataType = createSelector(widgetSelector, (widget) =>
  getWidgetDataType(widget),
);

export const selectWidgetDashboardData = createSelector(
  [selectDashboardData, selectWidgetDataType],
  (dashboardData, dataType) => dashboardData[dataType] || {},
);

export const selectWidgetIdentifier = createSelector(widgetSelector, (widget) =>
  getWidgetDataIdentifier(widget),
);

export const selectWidgetData = createSelector(
  [selectWidgetDashboardData, selectWidgetIdentifier],
  (dashboardData, widgetIdentifier) => dashboardData[widgetIdentifier],
);

/**
 * dataSelector
 * Selector for the data of a particular widget.
 * @param { Object } state: The application state.
 * @param { Object } props: Props containg i or id of the widget to get.
 * @returns { Object } The data of the widget matching the given props.
 */
export const dataSelector = createSelector(
  [
    selectWidgetData,
    selectWidgetIdentifier,
    selectWidgetType,
    selectWidgetDataType,
    selectCampaignFetchParamData,
  ],
  (widgetData, widgetIdentifier, widgetType, dataType, campaignData) => {
    switch (widgetType) {
      case widgetTypes.CAMPAIGNS_TABLE:
        return widgetData
          ? { ...widgetData, fetchParams: campaignData[widgetIdentifier] }
          : undefined;
      case widgetTypes.METRIC:
        return formatMetricWidgetData(widgetData, dataType);
      default:
        return {};
    }
  },
);

/**
 * layoutSelector
 * Selector for the layout data of a particular widget.
 * @param { Object } state: The application state.
 * @param { Object } props: Props containg i or id of the widget to get.
 * @returns { Object } The layout data of the widget matching the given props.
 */
export const layoutSelector = createSelector([selectLayout, propsSelector], (layout, { i, id }) =>
  layout.find((panel) => panel.i === id || panel.i === i),
);

/**
 * nameSelector
 * Selector for the name of a particular widget.
 * @param { Object } state: The application state.
 * @param { Object } props: Props containg i or id of the widget to get.
 * @returns { Object } The name of the widget matching the given props.
 */
export const nameSelector = (state, props) => {
  const widget = widgetSelector(state, props);
  const widgetType = getWidgetType(widget);
  switch (widgetType) {
    case widgetTypes.CAMPAIGNS_TABLE:
      return widget.name || getCampaignWidgetFilterString(widget);
    case widgetTypes.METRIC:
    default:
      return widget.name || getMetricName(getWidgetMetric(widget));
  }
};

export const selectIsWidgetFetchingCampaigns = (state, props) => {
  const fetchingCampaignsTableIds = selectFetchingCampaignsTableIds(state);
  const widget = widgetSelector(state, props);
  const widgetIdentifier = getWidgetDataIdentifier(widget);
  return fetchingCampaignsTableIds.indexOf(widgetIdentifier) !== -1;
};

/**
 * createWidgetSelector
 * Creates a selector for the state of a dashboard widget.
 * @param { Object } attr: The attribute to get from the widget state.
 * @returns { Selector } Selector for the widget of the given attribute.
 */
export const createWidgetSelector = (attr) =>
  createSelector(widgetSelector, (widget) => (attr ? widget[attr] : widget));

export const createWidgetSettingsSelector = (attr) =>
  createSelector(widgetSelector, ({ settings }) => settings[attr]);

/**
 * Selectors for the state of a given widget.
 * @param { Object } state: The application state.
 * @param { Object } props: Props containg i or id of the widget to get.
 * @returns { Any } The value of the determined widget state.
 */
export const selectDataForDashboardWidget = createSelector(dataSelector, (data) => data);
export const selectLayoutForDashboardWidget = createSelector(layoutSelector, (layout) => layout);
export const selectMetricForDashboardWidget = createWidgetSettingsSelector('metric');
export const selectNameForDashboardWidget = createSelector(nameSelector, (name) => name);
export const selectSettingsForDashboardWidget = createWidgetSelector('settings');
export const selectVisualizationForDashboardWidget = createWidgetSettingsSelector('visualization');
export const selectWidgetForDashboardWidget = createWidgetSelector();

/*------------------------------------------------------------------------------------------------*/
/* Metrics Selectors                                                                              */
/*------------------------------------------------------------------------------------------------*/
/**
 * metricsSelector
 * Selector for the metrics state.
 * @param { Object } state: The application state.
 * @returns { Object } The metrics state.
 */
export const metricsSelector = (state) => state.metrics;

/**
 * selectHighLevelMetrics
 * Selectors for getting a list of high level metrics.
 * @param { Object } state: The application state.
 * @returns { Array } A list of high level metrics.
 */
export const selectHighLevelMetrics = createSelector(metricsSelector, (metrics) =>
  metrics.highLevelMetrics
    .filter((metric) => metric.showForMultipleMediums)
    .filter((metric) => metric.enumName !== 'OpenRateSegmentAnalysis')
    .map((metric) => {
      const { enumName, name } = metric;
      return {
        ...metric,
        text: name,
        value: enumName,
      };
    }),
);
